import { addDays, format, parseISO } from "date-fns";

export type CreditInstallmentPlanRow = {
  installmentNo: number;
  label: string;
  dueDate: string;
  amount: number;
};

/** แบ่งยอดเท่า ๆ กัน N งวด — งวดสุดท้ายรับเศษ (ใช้ได้ทั้ง client และ server) */
export function buildEqualCreditInstallments(
  totalAmount: number,
  count: number,
  dueStartDate: string,
  intervalDays = 30,
): CreditInstallmentPlanRow[] {
  if (count < 1) throw new Error("Installment count must be at least 1");
  if (totalAmount <= 0) throw new Error("Total amount must be positive");

  const base = Math.floor(totalAmount / count);
  let allocated = 0;
  const amounts: number[] = [];
  for (let i = 0; i < count - 1; i++) {
    amounts.push(base);
    allocated += base;
  }
  amounts.push(totalAmount - allocated);

  const start = parseISO(dueStartDate);
  return amounts.map((amount, index) => ({
    installmentNo: index + 1,
    label: `ງວດ ${index + 1}/${count}`,
    dueDate: format(addDays(start, index * intervalDays), "yyyy-MM-dd"),
    amount,
  }));
}

export function normalizeInstallmentAmount(
  value: number | string | null | undefined,
): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.round(value) : 0;
  }
  if (value == null || value === "") return 0;
  const parsed = Number.parseInt(String(value).replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sumInstallmentAmounts(
  rows: ReadonlyArray<{ amount: number | string | null | undefined }>,
): number {
  return rows.reduce(
    (sum, row) => sum + normalizeInstallmentAmount(row.amount),
    0,
  );
}

export function getInstallmentPlanBalance(
  rows: ReadonlyArray<{ amount: number | string | null | undefined }>,
  totalAmount: number,
): { sum: number; total: number; balance: number; isValid: boolean } {
  const sum = sumInstallmentAmounts(rows);
  const total = Math.round(totalAmount);
  return {
    sum,
    total,
    balance: total - sum,
    isValid: rows.length >= 2 && sum === total,
  };
}

export function validateInstallmentPlanTotal(
  rows: ReadonlyArray<{ amount: number | string | null | undefined }>,
  totalAmount: number,
): boolean {
  return getInstallmentPlanBalance(rows, totalAmount).isValid;
}

export function formatInstallmentTotalMismatchMessage(
  sum: number,
  total: number,
): string {
  const diff = total - sum;
  if (diff > 0) {
    return `ຜົນລວມ ${sum.toLocaleString("lo-LA")} ກີບ · ຍັງຂາດ ${diff.toLocaleString("lo-LA")} ກີບ (ຕ້ອງ ${total.toLocaleString("lo-LA")} ກີບ)`;
  }
  if (diff < 0) {
    return `ຜົນລວມ ${sum.toLocaleString("lo-LA")} ກີບ · ເກີນ ${Math.abs(diff).toLocaleString("lo-LA")} ກີບ (ຕ້ອງ ${total.toLocaleString("lo-LA")} ກີບ)`;
  }
  return `ຜົນລວມ ${sum.toLocaleString("lo-LA")} ກີບ · ຖືກຕ້ອງ`;
}
